import styled from "@emotion/styled";
import Button from "@mui/material/Button";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import { TextField } from "src/components/Default/LekUI/TextField";
import newApi from "src/newApi";
import * as Yup from "yup";

import { useCheckToken } from "../../../../hooks/usePasswords";
import useQueryParams from "../../../../hooks/useQueryParams";
import { TextWithLink } from "../../../Default/LekUI/Layout/TextWithLink";

function SignUpForm(props) {
	const params = useQueryParams();
	const history = useHistory();
	const token = params.get("token");
	const { isLoading } = useCheckToken(token);
	const { t } = useTranslation();

	const {
		handleSubmit,
		handleChange: handleChangeFormik,
		values,
		errors,
		setErrors,
	} = useFormik({
		initialValues: {
			password: "",
			confirmPassword: "",
		},
		validateOnChange: false,
		onSubmit: async ({ password }) => {
			try {
				await newApi.setNewPassword(token, password);
				return history.push({
					pathname: generatePath(routing.paths.signIn),
					state: {
						passwordChanged: true,
					},
				});
			} catch (err) {
				if (Array.isArray(err?.response?.data?.password)) {
					setErrors({ password: err?.response?.data?.password[0] });
				} else {
					setErrors({ password: t("errors.somethingWentWrong") });
				}
			}
		},
		validationSchema: Yup.object().shape({
			password: Yup.string()
				.min(8, t("errors.passwordToShort"))
				.required(t("errors.requiredField")),
			confirmPassword: Yup.string().oneOf(
				[Yup.ref("password"), null],
				t("errors.passwordsNotTheSame"),
			),
		}),
	});

	if (isLoading) {
		return null;
	}

	const handleChange = (...params) => {
		setErrors({});
		handleChangeFormik(...params);
	};

	return (
		<>
			<p>{t("auth.resetPassword.setPassword.subtitle")}</p>
			<form onSubmit={handleSubmit} className={props.className}>
				<TextField
					variant="outlined"
					margin="normal"
					required
					name="password"
					label={t("auth.form.password")}
					type="password"
					id="password"
					autoComplete="current-password"
					onChange={handleChange}
					value={values.password}
					error={errors.password}
				/>
				<TextField
					variant="outlined"
					margin="normal"
					required
					name="confirmPassword"
					label={errors.confirmPassword || t("auth.form.passwordRepeat")}
					type="password"
					id="confirmPassword"
					autoComplete="new-password"
					onChange={handleChange}
					value={values.confirmPassword}
					error={errors.confirmPassword}
				/>
				<Button variant="contained" type="submit">
					{t("auth.resetPassword.setPassword.submit")}
				</Button>
			</form>
			<TextWithLink
				link={routing.paths.signIn}
				linkText={t("auth.signIn")}
				questionText={t("auth.signInQuestion")}
			/>
		</>
	);
}

export default styled(SignUpForm)`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;

  p {
    color: #49454f;
    font-weight: 500;
    margin: 8px 0;
  }

  & .Mui-error {
    color: red !important;

    fieldset {
      border-color: red !important;
    }
  }
`;
